import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  Crosshair, Loader2, RefreshCw, AlertTriangle, Check, Save, RotateCcw, Undo2, Trash2, Info,
} from 'lucide-react';
import { apiService, ConfigInspection, ConfigSectionsQuery, ContainerApplyResult } from '@/services/api.service';
import { useGameStore } from '@/stores/game.store';
import { ApplyReport, ContainerScope, NoReference } from '@/components/config/AllContainers';
import {
  GUN, LAUNCHER, MISSILE_LAUNCHER, NUMBER_FIELDS,
  buildSections, categoryMultiplier, categoryOf, cooldownOfRate, fireRate, headshotDamage, isAutomatic, isObject,
  listWeapons, nameList, normalizeRules, ruleProblems, unreadableRules, versionGaps, weaponProblems, weaponWarnings, withCategory,
  type LoadedSections, type NumberBound, type NumberField, type Problem, type TextField, type Weapon, type WeaponEdits,
  type WeaponRow, type WeaponRules, type WeaponsCatalog,
} from './weapons/weaponsModel';
import base from './LootboxPage.module.scss';
import styles from './WeaponsPage.module.scss';

// ============================================================
// Armes : sections weapons et weapon_rules du recubeicbm.config.json de RecubeICBM
// ============================================================
// Même mécanisme que les autres configs de mod (RecubeJobs) : la requête nomme le mod, le serveur ne
// retient que les containers dont le release-version.json porte RecubeICBM et son fichier, là où le
// mod le lit (dossier des mods). L'enregistrement écrit dans le dossier de travail de chaque container
// qui le porte ; le changement arrive sur les serveurs à la publication de l'image (bouton Envoyer),
// pas avant : rien n'est rechargé en direct.
//
// La page ne porte AUCUNE valeur du mod : valeurs par défaut des armes, valeur d'une clé absente,
// bornes du validateur, catégories et missiles arrivent avec l'inspection (`catalog`, table unique
// tenue côté serveur dans RecubeIcbmWeapons.java à partir de DefaultContent.cs du mod). Ici : les
// libellés, les unités et l'aide. Ce qui est écrit et ce qui ne l'est pas est décidé par
// weapons/weaponsModel.ts (une arme non touchée garde son entrée telle quelle, une arme revenue à ses
// valeurs par défaut quitte le fichier, missiles, tourelle et le reste du fichier ne sont ni lus ni écrits).
// ============================================================

const WEAPONS_FILE = 'recubeicbm.config.json';
const WEAPONS_MOD = 'RecubeICBM';

const WEAPONS_QUERY: ConfigSectionsQuery = {
  fileNames: [WEAPONS_FILE],
  keys: ['weapons', 'weapon_rules'],
  mod: WEAPONS_MOD,
};

const SECTION_LABELS: Record<string, string> = {
  weapons: 'armes',
  weapon_rules: 'règles communes',
};

const KIND_LABEL: Record<string, string> = {
  [GUN]: 'Arme à feu',
  [LAUNCHER]: 'Lanceur de roquettes',
  [MISSILE_LAUNCHER]: 'Lance-missiles',
};

const CATEGORY_LABEL: Record<string, string> = {
  pistol: 'Pistolet',
  automatic: 'Arme automatique',
  shotgun: 'Fusil à pompe',
};

/** Libellés des valeurs des règles à liste fermée ; une valeur sans libellé est montrée telle quelle. */
const RULE_CHOICE_LABEL: Record<string, Record<string, string>> = {
  claim_rule: {
    war: 'Zone libre, son pays, pays en guerre',
    any: 'Partout où le PvP le permet',
  },
  animation_signal_mode: {
    variant: 'Variante du joueur (mark_variant)',
    charged: 'Drapeau « chargé » (is_charged), tir seulement',
    both: 'Les deux',
  },
};

const categoryLabel = (id: string) => (id ? CATEGORY_LABEL[id] ?? id : 'Aucune');

/** Libellé, unité et aide d'un champ. Clé du fichier : `damage`, ou `params.pellets` pour une clé de params. */
interface FieldInfo {
  label: string;
  unit?: string;
  help: string;
}

const FIELD_INFO: Record<string, FieldInfo> = {
  name: { label: 'Nom affiché', help: "Nom de l'arme dans les messages et la description de l'objet. Vide : son identifiant." },
  item: { label: 'Objet', help: "Objet qui déclenche l'arme, espace:nom en minuscules (ex. ng:ak47). Il doit exister dans le pack." },
  damage: { label: 'Dégâts par balle', unit: 'points de vie', help: "Dégâts d'une balle (d'un plomb pour un fusil à pompe, du coup direct pour une roquette), avant la baisse avec la distance. 20 points de vie = 10 cœurs." },
  'params.headshot_damage': { label: 'Dégâts à la tête', unit: 'points de vie', help: "Dégâts d'une balle qui touche la tête. 0 : dégâts par balle × multiplicateur de tête." },
  'params.headshot_multiplier': { label: 'Multiplicateur de tête', unit: '×', help: 'Ne sert que si les dégâts à la tête valent 0. 0 : pas de bonus.' },
  cooldown_seconds: { label: 'Délai entre deux tirs', unit: 'secondes', help: "Délai minimal entre deux tirs (ou deux rafales) : c'est la valeur écrite dans le fichier. 1 / cadence." },
  range: { label: 'Portée', unit: 'blocs', help: 'Portée maximale : au-delà, la balle ne blesse plus.' },
  'params.spread_degrees': { label: 'Dispersion', unit: 'degrés', help: 'Écart du tir autour de la visée.' },
  'params.auto': { label: 'Tir automatique', help: "Une pression tire à la cadence tant que le clic reste enfoncé." },
  'params.trigger_hold_ms': { label: 'Maintien de la détente', unit: 'millisecondes', help: 'Tir automatique : durée pendant laquelle une pression continue le tir.' },
  'params.burst': { label: 'Balles par rafale', unit: 'balles', help: '1 : coup par coup. Ignoré en tir automatique.' },
  'params.burst_interval_ticks': { label: 'Intervalle dans la rafale', unit: 'ticks', help: "Ticks entre deux balles d'une rafale (20 ticks = 1 seconde)." },
  'params.pellets': { label: 'Plombs par tir', unit: 'plombs', help: 'Fusil à pompe : nombre de plombs tirés pour une seule munition. 1 : une balle.' },
  'params.pellet_spread_degrees': { label: 'Dispersion des plombs', unit: 'degrés', help: "Cône des plombs autour de l'axe du tir (avec plus d'un plomb)." },
  'params.falloff_start': { label: 'Distance sans perte de dégâts', unit: 'blocs', help: "Distance jusqu'à laquelle la balle garde tous ses dégâts." },
  'params.min_damage_ratio': { label: 'Dégâts gardés à la portée maximale', unit: 'part de 0 à 1', help: '1 : aucune baisse des dégâts avec la distance. 0,5 : la moitié des dégâts à la portée maximale.' },
  magazine: { label: 'Balles par chargeur', unit: 'balles', help: "Balles d'un chargeur plein." },
  ammo_item: { label: 'Chargeur plein', help: "Objet consommé à chaque rechargement (pour un lanceur : la roquette), espace:nom en minuscules." },
  empty_magazine: { label: 'Chargeur vide rendu', help: "Objet rendu à la fin d'un rechargement et quand l'arme casse. Vide : rien n'est rendu." },
  reload_seconds: { label: 'Rechargement', unit: 'secondes', help: "Durée d'un rechargement (1 seconde = 20 ticks)." },
  'params.auto_reload': { label: 'Rechargement automatique', help: "Recharge dès que le chargeur est vide, si un chargeur plein est dans l'inventaire." },
  uses: { label: 'Durabilité', help: "Nombre de tirs (ou de rechargements) avant que l'arme casse. 0 : illimitée." },
  'params.wear_per_shot': { label: 'Durabilité comptée en tirs', help: "Décochée : comptée en rechargements, l'arme casse quand son dernier chargeur est vide." },
  'params.recoil': { label: 'Recul du tireur', help: "Élan vers l'arrière donné au tireur à chaque tir." },
  'params.knockback': { label: 'Élan donné à la cible', help: 'Une fois par tir et par cible.' },
  'params.hit_outline': { label: 'Marge de touche', unit: 'blocs', help: 'Marge autour des entités pour toucher : la taille de la balle.' },
  'params.hit_cooldown_ticks': { label: 'Invulnérabilité après une balle', unit: 'ticks', help: '0 : chaque balle compte.' },
  projectile_speed: { label: 'Vitesse du projectile', unit: 'blocs par tick', help: 'Vitesse de la roquette ou du missile tiré.' },
  explosion_power: { label: "Puissance de l'explosion", help: "Explosion de la roquette à l'impact." },
  'params.splash_radius': { label: 'Rayon du souffle', unit: 'blocs', help: "Rayon des dégâts de l'explosion." },
  'params.splash_damage': { label: 'Dégâts du souffle', unit: 'points de vie', help: "Dégâts au centre de l'explosion, décroissants jusqu'au bord." },
  'params.break_blocks': { label: "L'explosion casse des blocs", help: 'Seulement hors claim, hors région et hors spawn.' },
  'params.fire_seconds': { label: 'Feu sur les cibles touchées', unit: 'secondes', help: "Durée du feu sur les entités touchées par l'explosion. 0 : aucun." },
  'params.gravity': { label: 'Gravité du projectile', unit: 'blocs par tick²', help: '0 : ligne droite.' },
  'params.visual_variant': { label: 'Variante du modèle', help: 'Modèle de la roquette dans le pack (index du render controller).' },
  'params.max_missile_tier': { label: 'Tier maximal du missile tiré', help: 'Le lance-missiles tire le premier missile de ce tier ou moins. Un T4 ne se tire jamais à la main.' },
  'params.require_enemy_claim': { label: 'Depuis un territoire ennemi seulement', help: 'Le tireur doit se tenir dans un territoire ennemi.' },
  sound: { label: 'Son du tir', help: 'Son du pack RecubeICBM (ou son vanilla) joué à chaque tir. Vide : aucun.' },
  reload_sound: { label: 'Son du rechargement', help: 'Vide : aucun.' },
  'params.sound_volume': { label: 'Volume du son de tir', unit: 'de 0 à 1', help: '0 : tir muet. Le moteur refuse un volume plus fort que 1.' },
  'params.sound_pitch': { label: 'Hauteur du son de tir', help: '1 : le son tel quel.' },
  levels: { label: 'Mondes permis', help: "Mondes où l'arme tire, séparés par des virgules. Vide : tous. Motifs avec *, casse ignorée (earth, moon_*)." },
  denied_levels: { label: 'Mondes interdits', help: "Mondes où l'arme ne tire pas, même s'ils sont permis (DIM-28, moon_*)." },
};

/** Types d'arme pour lesquels un champ a un effet. Absent : tous. */
const FIELD_KINDS: Record<string, string[]> = {
  damage: [GUN, LAUNCHER],
  'params.headshot_damage': [GUN],
  'params.headshot_multiplier': [GUN],
  'params.spread_degrees': [GUN],
  'params.auto': [GUN],
  'params.trigger_hold_ms': [GUN],
  'params.burst': [GUN],
  'params.burst_interval_ticks': [GUN],
  'params.pellets': [GUN],
  'params.pellet_spread_degrees': [GUN],
  'params.falloff_start': [GUN],
  'params.min_damage_ratio': [GUN],
  magazine: [GUN],
  ammo_item: [GUN, LAUNCHER],
  empty_magazine: [GUN],
  reload_seconds: [GUN],
  'params.auto_reload': [GUN],
  'params.wear_per_shot': [GUN],
  'params.recoil': [GUN],
  'params.knockback': [GUN, LAUNCHER],
  'params.hit_outline': [GUN],
  'params.hit_cooldown_ticks': [GUN],
  projectile_speed: [LAUNCHER, MISSILE_LAUNCHER],
  'params.gravity': [LAUNCHER, MISSILE_LAUNCHER],
  explosion_power: [LAUNCHER],
  'params.splash_radius': [LAUNCHER],
  'params.splash_damage': [LAUNCHER],
  'params.break_blocks': [LAUNCHER],
  'params.fire_seconds': [LAUNCHER],
  'params.visual_variant': [LAUNCHER],
  'params.max_missile_tier': [MISSILE_LAUNCHER],
  'params.require_enemy_claim': [MISSILE_LAUNCHER],
  extra_missiles: [MISSILE_LAUNCHER],
  reload_sound: [GUN],
  'params.sound_volume': [GUN],
  'params.sound_pitch': [GUN],
};

/** Sections de la fiche, dans l'ordre. `rate` est la cadence, calculée à partir du délai entre deux tirs. */
const GROUPS: Array<{ title: string; note?: string; fields: string[] }> = [
  { title: 'Tir', fields: ['damage', 'params.headshot_damage', 'params.headshot_multiplier', 'rate', 'cooldown_seconds', 'range', 'params.spread_degrees'] },
  { title: 'Mode de tir et plombs', fields: ['params.auto', 'params.trigger_hold_ms', 'params.burst', 'params.burst_interval_ticks', 'params.pellets', 'params.pellet_spread_degrees'] },
  { title: 'Dégâts selon la distance', fields: ['params.falloff_start', 'params.min_damage_ratio'] },
  { title: 'Chargeur et rechargement', fields: ['magazine', 'ammo_item', 'empty_magazine', 'reload_seconds', 'params.auto_reload'] },
  { title: 'Durabilité', fields: ['uses', 'params.wear_per_shot'] },
  { title: 'Projectile et explosion', fields: ['projectile_speed', 'params.gravity', 'explosion_power', 'params.splash_radius', 'params.splash_damage', 'params.break_blocks', 'params.fire_seconds', 'params.visual_variant'] },
  { title: 'Missiles tirés à la main', fields: ['params.max_missile_tier', 'params.require_enemy_claim', 'extra_missiles'] },
  { title: 'Recul et impact', fields: ['params.recoil', 'params.knockback', 'params.hit_outline', 'params.hit_cooldown_ticks'] },
  { title: 'Sons', fields: ['sound', 'reload_sound', 'params.sound_volume', 'params.sound_pitch'] },
  { title: 'Mondes', note: "S'ajoutent aux mondes interdits à toutes les armes (onglet Règles communes).", fields: ['levels', 'denied_levels'] },
];

const RULE_INFO: Record<string, FieldInfo> = {
  enabled: { label: 'Armes actives', help: 'Interrupteur de toutes les armes. /icbm off les coupe aussi.' },
  require_permission: { label: 'Permission exigée', help: 'Chaque arme demande la permission recubeicbm.weapon.<id>.' },
  claim_rule: { label: 'Où le tireur peut se tenir', help: 'En zone libre, dans son pays ou dans un pays en guerre avec le sien ; ou partout où le PvP le permet.' },
  war_cache_seconds: { label: "Mémoire de l'état de guerre", unit: 'secondes', help: "Durée pendant laquelle l'état de guerre entre deux pays est gardé en mémoire pour les armes." },
  respect_server_pvp: { label: 'Respecter le PvP du serveur', help: 'Sans PvP sur le serveur, les armes ne blessent pas les joueurs.' },
  max_rockets_per_level: { label: 'Projectiles en vol par monde', unit: 'projectiles', help: 'Roquettes et missiles tirés à la main en vol en même temps, par monde.' },
  tracer_particles: { label: 'Particules de trace par tir', unit: 'particules', help: "Réparties entre les plombs d'un fusil à pompe. 0 : aucune trace." },
  headshots: { label: 'Tirs à la tête', help: 'Décoché : aucune balle ne fait de dégâts de tête.' },
  headshot_below_eye: { label: 'Seuil de la tête sous les yeux', unit: 'blocs', help: 'Une balle compte à la tête si elle touche au-dessus des yeux de la cible moins ce seuil.' },
  headshot_sound: { label: "Son d'un tir à la tête", help: 'Joué au seul tireur. Vide : aucun.' },
  denied_levels: { label: 'Mondes interdits à toutes les armes', help: 'Séparés par des virgules. Motifs avec *, casse ignorée (DIM-28, moon_*). Chaque arme a aussi les siens.' },
  hit_mobs: { label: 'Les balles touchent aussi les mobs', help: 'Décoché : seuls les joueurs sont touchés. Les roquettes ne sont pas concernées.' },
  animation_signal: { label: "Signal d'animation", help: "Signal posé sur le joueur qui tire ou recharge, lu par le resource pack pour animer l'arme tenue. Décoché : les armes restent à l'animation de repos." },
  animation_signal_mode: { label: "Voie du signal d'animation", help: 'Doit correspondre à ce que lit le resource pack installé.' },
  auto_sync_shots: { label: "Renvoi de l'objet en tir automatique", unit: 'tirs', help: "L'objet (munitions, durabilité) est renvoyé au client tous les N tirs. 0 : seulement quand le tir s'arrête." },
};

/** Sections de l'onglet Règles communes ; une règle du catalogue absente d'ici est montrée à la fin. */
const RULE_GROUPS: Array<{ title: string; fields: string[] }> = [
  { title: 'Tirs à la tête', fields: ['headshots', 'headshot_below_eye', 'headshot_sound'] },
  { title: 'Mondes', fields: ['denied_levels'] },
  { title: 'Usage des armes', fields: ['enabled', 'require_permission', 'respect_server_pvp', 'hit_mobs', 'claim_rule', 'war_cache_seconds'] },
  { title: 'Réglages techniques', fields: ['max_rockets_per_level', 'tracer_particles', 'auto_sync_shots', 'animation_signal', 'animation_signal_mode'] },
];

/** Hauteur des yeux d'un joueur debout, pour dire le seuil de tête en hauteur depuis les pieds. */
const PLAYER_EYE_HEIGHT = 1.62;

const numberFormat = new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 6 });
const fmt = (n: number | null | undefined) => (typeof n === 'number' && Number.isFinite(n) ? numberFormat.format(n) : '?');

function boundText(b: NumberBound | undefined): string {
  if (!b) return '';
  const range = b.max === undefined ? `${fmt(b.min)} ou plus` : `${fmt(b.min)} à ${fmt(b.max)}`;
  return b.integer ? `${range}, entier` : range;
}

const isParam = (key: string) => key.startsWith('params.');
const paramKey = (key: string) => key.slice('params.'.length);
const isNumberField = (key: string): key is NumberField => (NUMBER_FIELDS as readonly string[]).includes(key);

function numberOf(w: Weapon, key: string): number {
  if (isParam(key)) return w.params[paramKey(key)];
  return isNumberField(key) ? w[key] : Number.NaN;
}

function withNumber(w: Weapon, key: string, v: number): Weapon {
  if (isParam(key)) return { ...w, params: { ...w.params, [paramKey(key)]: v } };
  return isNumberField(key) ? { ...w, [key]: v } : w;
}

function sameStrings(a: string[], b: string[]): boolean {
  return a.length === b.length && a.every((x, i) => x === b[i]);
}

/**
 * Champ numérique : garde ce qui est tapé tant que le champ a le focus (« 0, » en cours de frappe), et
 * rend NaN pour un champ vide, que les contrôles signalent.
 */
function NumInput({ value, onChange, step, placeholder, disabled, ariaLabel }: {
  value: number;
  onChange: (v: number) => void;
  step?: number | 'any';
  placeholder?: string;
  disabled?: boolean;
  ariaLabel?: string;
}) {
  const [draft, setDraft] = useState<string | null>(null);
  const shown = draft ?? (Number.isFinite(value) ? String(value) : '');
  return (
    <input type="number" inputMode="decimal" step={step ?? 'any'} value={shown} placeholder={placeholder}
      disabled={disabled} aria-label={ariaLabel}
      onChange={(e) => {
        const raw = e.target.value;
        setDraft(raw);
        onChange(raw.trim() === '' ? Number.NaN : Number(raw));
      }}
      onBlur={() => setDraft(null)} />
  );
}

/** Liste de noms saisie en texte, séparés par des virgules. */
function NamesInput({ value, onChange, placeholder, ariaLabel }: {
  value: string[];
  onChange: (v: string[]) => void;
  placeholder?: string;
  ariaLabel?: string;
}) {
  const [draft, setDraft] = useState<string | null>(null);
  return (
    <input value={draft ?? value.join(', ')} placeholder={placeholder} aria-label={ariaLabel}
      onChange={(e) => {
        setDraft(e.target.value);
        onChange(nameList(e.target.value.split(',')));
      }}
      onBlur={() => setDraft(null)} />
  );
}

/** Enveloppe d'un champ : libellé et bornes, saisie, erreur ou avertissement, aide, retour à la valeur par défaut. */
function Field({ label, bounds, help, title, problem, warning, wide, group, extra, children, onDefault, defaultText }: {
  label: string;
  bounds?: string;
  help?: string;
  title?: string;
  problem?: string;
  warning?: string;
  wide?: boolean;
  /** Plusieurs cases à cocher : chacune a son propre libellé, le champ n'en est pas un. */
  group?: boolean;
  /** Contenu placé sous la saisie (raccourci propre au champ). */
  extra?: ReactNode;
  children: ReactNode;
  /** Remet le champ à la valeur par défaut du mod (montré seulement quand il en diffère). */
  onDefault?: () => void;
  defaultText?: string;
}) {
  return (
    <div className={`${styles.field} ${wide ? styles.fieldWide : ''}`} title={title}>
      {group ? (
        <div className={styles.field}>
          <span className={styles.fieldLabel}>{label}{bounds && <em>{bounds}</em>}</span>
          {children}
        </div>
      ) : (
        <label className={styles.field}>
          <span className={styles.fieldLabel}>{label}{bounds && <em>{bounds}</em>}</span>
          {children}
        </label>
      )}
      {extra}
      {problem && <span className={styles.fieldError}>{problem}</span>}
      {!problem && warning && <span className={styles.fieldWarn}>{warning}</span>}
      {help && <span className={styles.fieldHelp}>{help}</span>}
      {onDefault && (
        <button type="button" className={styles.defaultBtn} onClick={onDefault}>
          valeur par défaut : {defaultText}
        </button>
      )}
    </div>
  );
}

export function WeaponsPage() {
  const activeGameId = useGameStore((s) => s.activeGameId);
  const [inspection, setInspection] = useState<ConfigInspection | null>(null);
  const [catalog, setCatalog] = useState<WeaponsCatalog | null>(null);
  /** Sections telles que lues dans le fichier de référence (une section absente reste absente). */
  const [loaded, setLoaded] = useState<LoadedSections | null>(null);
  const [edits, setEdits] = useState<WeaponEdits>({});
  const [rules, setRules] = useState<WeaponRules>({});
  /** Les entrées que le mod ignorerait (sans id, id en double) seront retirées à l'enregistrement. */
  const [dropInvalid, setDropInvalid] = useState(false);
  const [selected, setSelected] = useState<string | null>(null);
  const [tab, setTab] = useState<'weapons' | 'rules'>('weapons');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState<{ ok: boolean; msg: string } | null>(null);
  const [report, setReport] = useState<ContainerApplyResult | null>(null);
  /** Dernière lecture lancée : une réponse plus ancienne (projet précédent) est ignorée. */
  const loadSeq = useRef(0);

  /**
   * Lit les deux sections dans le container de référence (le premier, par id, dont l'image porte
   * RecubeICBM et son fichier), la table du mod, et l'alignement des autres containers. `keepEdit` ne
   * rafraîchit que le bandeau, après un enregistrement.
   */
  const load = useCallback(async (keepEdit = false) => {
    const seq = ++loadSeq.current;
    setLoading(true);
    if (!keepEdit) {
      setStatus(null);
      setReport(null);
      setLoaded(null);
      setCatalog(null);
    }
    const r = await apiService.inspectContainerConfig(WEAPONS_QUERY);
    if (seq !== loadSeq.current) return;
    setLoading(false);
    setInspection(r.data ?? null);
    if (keepEdit) return;
    if (!r.data) { setStatus({ ok: false, msg: r.error ?? 'Lecture impossible.' }); return; }
    if (!r.data.referenceContainerId) return; // aucun container ne porte le mod et son fichier : dit plus bas
    const table = r.data.catalog as WeaponsCatalog | null | undefined;
    if (!table || !Array.isArray(table.weapons) || !Array.isArray(table.params)) {
      setStatus({ ok: false, msg: "Le serveur n'a pas renvoyé la table des armes de RecubeICBM : l'API du panel est à mettre à jour." });
      return;
    }
    const raw = (r.data.sections ?? {}) as Record<string, unknown>;
    const file = r.data.referencePath ?? WEAPONS_FILE;
    if (raw.weapons != null && !Array.isArray(raw.weapons)) {
      setStatus({ ok: false, msg: `${file} : weapons n'a pas la forme attendue (liste). Corrige le fichier dans Paramètres généraux → Containers.` });
      return;
    }
    if (raw.weapon_rules != null && !isObject(raw.weapon_rules)) {
      setStatus({ ok: false, msg: `${file} : weapon_rules n'a pas la forme attendue (objet). Corrige le fichier dans Paramètres généraux → Containers.` });
      return;
    }
    // Une section à null vaut une section absente pour le mod : toutes ses valeurs par défaut.
    const next: LoadedSections = {};
    if (raw.weapons != null) next.weapons = raw.weapons;
    if (raw.weapon_rules != null) next.weapon_rules = raw.weapon_rules;
    setCatalog(table);
    setLoaded(next);
    setEdits({});
    setRules(normalizeRules(next.weapon_rules, table));
    setDropInvalid(false);
    setSelected(null);
  }, []);

  useEffect(() => { void load(); }, [activeGameId, load]);

  const list = useMemo(
    () => (loaded && catalog ? listWeapons(loaded.weapons, edits, catalog) : { rows: [], invalid: [] }),
    [loaded, edits, catalog],
  );
  const rows = list.rows;

  /**
   * Sections qui seraient envoyées : la page est modifiée quand elles diffèrent de celles du fichier.
   * Un fichier qui porte une valeur illisible pour le mod est donc « à enregistrer » dès l'ouverture.
   */
  const sections = useMemo(
    () => (loaded && catalog ? buildSections(loaded, edits, rules, catalog, dropInvalid) : {}),
    [loaded, edits, rules, catalog, dropInvalid],
  );
  const dirty = useMemo(() => !!loaded && !!catalog
    && (JSON.stringify(sections.weapons) !== JSON.stringify(loaded.weapons)
      || JSON.stringify(sections.weapon_rules) !== JSON.stringify(loaded.weapon_rules)),
  [loaded, catalog, sections]);
  /** Valeurs du fichier que le mod ne saurait pas lire : la page les remplace par ce qu'elle affiche. */
  const unreadable = useMemo(() => {
    if (!loaded || !catalog) return [];
    const out = rows.filter((r) => r.unreadable.length > 0 && !r.removed).map((r) => `${r.value.name || r.id} (${r.unreadable.join(', ')})`);
    const badRules = unreadableRules(loaded.weapon_rules, catalog);
    if (badRules.length > 0) out.push(`règles communes (${badRules.join(', ')})`);
    return out;
  }, [loaded, catalog, rows]);

  /** Ce que le serveur refuserait, arme par arme : seules les armes écrites dans le fichier comptent. */
  const problemsByKey = useMemo(() => {
    const out: Record<string, Problem[]> = {};
    if (!catalog) return out;
    const applied = rows.filter((r) => !r.removed).map((r) => r.value);
    for (const r of rows) {
      if (r.entry) out[r.key] = weaponProblems(r.value, catalog, applied);
    }
    return out;
  }, [rows, catalog]);
  const rulesBad = useMemo(() => (catalog ? ruleProblems(rules, catalog) : []), [rules, catalog]);
  const blockingRows = rows.filter((r) => (problemsByKey[r.key]?.length ?? 0) > 0);
  const invalidLeft = !dropInvalid && list.invalid.length > 0;
  const blocked = blockingRows.length > 0 || rulesBad.length > 0 || invalidLeft;

  const row = rows.find((r) => r.key === selected) ?? null;

  // ==================== Modifications ====================

  /** Modifie une arme (les valeurs affichées deviennent celles de la page). */
  const editWeapon = (r: WeaponRow, fn: (w: Weapon) => Weapon) => {
    const value = fn(r.value);
    setEdits((prev) => ({ ...prev, [r.key]: { value, reset: prev[r.key]?.reset } }));
  };

  /** Retour aux valeurs par défaut du mod : l'entrée de l'arme quittera le fichier. */
  const resetWeapon = (r: WeaponRow) => {
    if (!r.def) return;
    const value = r.def.value;
    setEdits((prev) => ({ ...prev, [r.key]: { value, reset: true } }));
  };

  /** Oublie ce que la page a changé sur cette arme : retour à ce qui est dans le fichier. */
  const revertWeapon = (r: WeaponRow) => {
    setEdits((prev) => Object.fromEntries(Object.entries(prev).filter(([k]) => k !== r.key)));
  };

  /** Retire une arme sur mesure du fichier. */
  const removeWeapon = (r: WeaponRow) => {
    if (!window.confirm(`Retirer l'arme « ${r.value.name || r.id} » (${r.id}) du fichier ? Elle n'existera plus pour le mod à la prochaine publication.`)) return;
    setEdits((prev) => ({ ...prev, [r.key]: { value: r.value, removed: true } }));
  };

  /** Multiplicateur de tête de toutes les armes d'une catégorie (le mod n'a pas de réglage par catégorie). */
  const setCategoryMultiplier = (category: string, v: number) => {
    setEdits((prev) => {
      const out = { ...prev };
      for (const r of rows) {
        if (r.removed || r.value.category !== category) continue;
        out[r.key] = { value: { ...r.value, params: { ...r.value.params, headshot_multiplier: v } }, reset: prev[r.key]?.reset };
      }
      return out;
    });
  };

  const setRule = (key: string, value: WeaponRules[string]) => setRules((prev) => ({ ...prev, [key]: value }));

  // ==================== Sauvegarde ====================

  /**
   * Écrit les sections dans chaque container du projet dont l'image porte RecubeICBM et son fichier.
   * Rien n'est publié : le changement part avec l'image, au prochain Envoyer.
   */
  const save = async () => {
    if (!loaded || !catalog || !dirty || blocked) return;
    setSaving(true);
    setStatus(null);
    setReport(null);
    const sent = sections;
    const res = await apiService.applyContainerConfig({ ...WEAPONS_QUERY, sections: sent });
    setSaving(false);
    if (!res.data) {
      setStatus({ ok: false, msg: res.error ?? 'Enregistrement en échec' });
      return;
    }
    setReport(res.data);
    // Un container en échec garde la page « non enregistrée » : un nouvel essai réécrit partout.
    if (res.data.success) {
      const next: LoadedSections = { ...loaded };
      if (sent.weapons !== undefined) next.weapons = sent.weapons;
      if (sent.weapon_rules !== undefined) next.weapon_rules = sent.weapon_rules;
      setLoaded(next);
      setEdits({});
      setRules(normalizeRules(next.weapon_rules, catalog));
      setDropInvalid(false);
    }
    void load(true);
  };

  /** Relit le fichier ; ne jette pas des modifications sans le demander. */
  const reload = () => {
    if (dirty && !window.confirm(`Recharger ${WEAPONS_FILE} abandonnera les modifications non enregistrées. Continuer ?`)) return;
    void load();
  };

  /** Lecture faite, mais aucun container du projet ne porte le mod et son fichier. */
  const missing = inspection && !inspection.referenceContainerId ? inspection : null;
  const ready = !!loaded && !!catalog;
  /** Sections que l'enregistrement n'écrira pas : absentes du fichier, et rien à y mettre. */
  const untouched = ready ? WEAPONS_QUERY.keys.filter((k) => !(k in sections)) : [];
  const gaps = catalog ? versionGaps(catalog) : [];

  // ==================== Fiche d'une arme ====================

  const renderSheet = (r: WeaponRow, table: WeaponsCatalog) => {
    const w = r.value;
    const def = r.def?.value;
    const problems = problemsByKey[r.key] ?? [];
    const warnings = weaponWarnings(w);
    const problemOf = (field: string) => problems.find((p) => p.field === field)?.message;
    const warningOf = (field: string) => warnings.find((p) => p.field === field)?.message;
    const applies = (key: string) => !FIELD_KINDS[key] || FIELD_KINDS[key].includes(w.kind);
    const category = categoryOf(table, w.category);
    const rate = fireRate(w.cooldown_seconds);
    const defRate = def ? fireRate(def.cooldown_seconds) : null;

    const numberField = (key: string) => {
      const info = FIELD_INFO[key] ?? { label: key, help: '' };
      const bound = isParam(key) ? table.params.find((p) => p.key === paramKey(key)) : table.fields[key];
      const value = numberOf(w, key);
      const defValue = def ? numberOf(def, key) : undefined;
      let unit = info.unit;
      let help = info.help;
      if (key === 'uses') unit = w.kind === GUN && !(w.params.wear_per_shot > 0) ? 'rechargements' : 'tirs';
      if (key === 'reload_seconds' && Number.isFinite(value)) help = `${info.help} Ici : ${fmt(Math.round(value * 20))} ticks.`;
      if (key === 'params.headshot_multiplier') {
        help = `${info.help} Catégorie ${categoryLabel(w.category).toLowerCase()} : × ${fmt(categoryMultiplier(table, w.category))}.`;
      }
      if (key === 'params.headshot_damage') help = `${info.help} Appliqué ici : ${fmt(headshotDamage(w))} points de vie à la tête.`;
      if (key === 'damage' && (w.params.pellets ?? 1) > 1) help = `${info.help} Ici : ${fmt(w.params.pellets)} plombs par tir.`;
      return (
        <Field key={key} label={info.label} bounds={boundText(bound)} help={help} title={key}
          problem={problemOf(key)} warning={warningOf(key)}
          onDefault={defValue !== undefined && !Object.is(defValue, value) ? () => editWeapon(r, (x) => withNumber(x, key, defValue)) : undefined}
          defaultText={fmt(defValue)}>
          <div className={`${styles.inputUnit} ${problemOf(key) ? styles.inputBad : ''}`}>
            <NumInput value={value} step={bound?.integer ? 1 : 'any'} onChange={(v) => editWeapon(r, (x) => withNumber(x, key, v))} />
            {unit && <span>{unit}</span>}
          </div>
        </Field>
      );
    };

    const flagField = (key: string) => {
      const info = FIELD_INFO[key] ?? { label: key, help: '' };
      const on = numberOf(w, key) > 0;
      const defOn = def ? numberOf(def, key) > 0 : undefined;
      return (
        <Field key={key} label={info.label} help={info.help} title={key} problem={problemOf(key)} warning={warningOf(key)}
          onDefault={defOn !== undefined && defOn !== on ? () => editWeapon(r, (x) => withNumber(x, key, defOn ? 1 : 0)) : undefined}
          defaultText={defOn ? 'oui' : 'non'}>
          <span className={styles.check}>
            <input type="checkbox" checked={on} onChange={(e) => editWeapon(r, (x) => withNumber(x, key, e.target.checked ? 1 : 0))} />
            {on ? 'Oui' : 'Non'}
          </span>
        </Field>
      );
    };

    const textField = (key: TextField, opts: { label?: string; placeholder?: string; extra?: ReactNode } = {}) => {
      const info = FIELD_INFO[key] ?? { label: key, help: '' };
      const defValue = def?.[key];
      return (
        <Field key={key} label={opts.label ?? info.label} help={info.help} title={key} problem={problemOf(key)} extra={opts.extra}
          onDefault={defValue !== undefined && defValue !== w[key] ? () => editWeapon(r, (x) => ({ ...x, [key]: defValue })) : undefined}
          defaultText={defValue === '' ? 'vide' : defValue}>
          <div className={`${styles.inputUnit} ${problemOf(key) ? styles.inputBad : ''}`}>
            <input value={w[key]} placeholder={opts.placeholder} spellCheck={false}
              onChange={(e) => editWeapon(r, (x) => ({ ...x, [key]: e.target.value }))} />
          </div>
        </Field>
      );
    };

    const namesField = (key: 'levels' | 'denied_levels') => {
      const info = FIELD_INFO[key];
      const defValue = def?.[key];
      return (
        <Field key={key} label={info.label} help={info.help} title={key} wide
          onDefault={defValue && !sameStrings(defValue, w[key]) ? () => editWeapon(r, (x) => ({ ...x, [key]: [...defValue] })) : undefined}
          defaultText={defValue && defValue.length > 0 ? defValue.join(', ') : 'aucun'}>
          <div className={styles.inputUnit}>
            <NamesInput value={w[key]} placeholder={key === 'levels' ? 'tous les mondes' : 'aucun'}
              onChange={(v) => editWeapon(r, (x) => ({ ...x, [key]: v }))} />
          </div>
        </Field>
      );
    };

    const rateField = () => (
      <Field key="rate" label="Cadence" help="Tirs par seconde, calculés à partir du délai entre deux tirs. Modifier la cadence règle le délai (1 / cadence)."
        title="1 / cooldown_seconds" problem={problemOf('cooldown_seconds') ? 'voir le délai entre deux tirs' : undefined}
        onDefault={def && !Object.is(def.cooldown_seconds, w.cooldown_seconds) ? () => editWeapon(r, (x) => ({ ...x, cooldown_seconds: def.cooldown_seconds })) : undefined}
        defaultText={defRate === null ? 'sans délai' : `${fmt(defRate)} tirs par seconde`}>
        <div className={`${styles.inputUnit} ${problemOf('cooldown_seconds') ? styles.inputBad : ''}`}>
          <NumInput value={rate ?? Number.NaN} placeholder={w.cooldown_seconds === 0 ? 'sans délai' : undefined}
            onChange={(v) => editWeapon(r, (x) => ({ ...x, cooldown_seconds: cooldownOfRate(v) }))} />
          <span>tirs par seconde{isAutomatic(w) ? ', en continu' : ''}</span>
        </div>
      </Field>
    );

    const missilesField = () => {
      const choices = table.missiles.filter((m) => m.tier <= table.handMaxMissileTier);
      const strangers = w.extra_missiles.filter((id) => !choices.some((m) => m.id.toLowerCase() === id.toLowerCase()));
      const has = (id: string) => w.extra_missiles.some((x) => x.toLowerCase() === id.toLowerCase());
      const toggle = (id: string, on: boolean) => editWeapon(r, (x) => ({
        ...x,
        extra_missiles: on ? [...x.extra_missiles, id] : x.extra_missiles.filter((m) => m.toLowerCase() !== id.toLowerCase()),
      }));
      return (
        <Field key="extra_missiles" label="Missiles tirables en plus" title="extra_missiles" wide group problem={problemOf('extra_missiles')}
          help="Missiles que l'arme tire même au-dessus de son tier maximal. Un T4 ne se tire jamais à la main."
          onDefault={def && !sameStrings(def.extra_missiles, w.extra_missiles) ? () => editWeapon(r, (x) => ({ ...x, extra_missiles: [...def.extra_missiles] })) : undefined}
          defaultText={def && def.extra_missiles.length > 0 ? def.extra_missiles.join(', ') : 'aucun'}>
          <div className={styles.chips}>
            {choices.map((m) => (
              <label key={m.id} className={styles.check}>
                <input type="checkbox" checked={has(m.id)} onChange={(e) => toggle(m.id, e.target.checked)} />
                {m.name} (T{m.tier})
              </label>
            ))}
            {strangers.map((id) => (
              <label key={id} className={styles.check}>
                <input type="checkbox" checked onChange={() => toggle(id, false)} />
                {id} (inconnu ou T4)
              </label>
            ))}
          </div>
        </Field>
      );
    };

    const renderField = (key: string) => {
      if (key === 'rate') return rateField();
      if (key === 'extra_missiles') return missilesField();
      if (key === 'levels' || key === 'denied_levels') return namesField(key);
      if (key === 'ammo_item') return textField(key, { label: w.kind === LAUNCHER ? 'Munition (roquette)' : undefined, placeholder: 'ng:chargeur_...' });
      if (key === 'empty_magazine') {
        return textField(key, {
          placeholder: 'aucun chargeur rendu',
          extra: category && category.emptyMagazine !== w.empty_magazine ? (
            <button type="button" className={styles.defaultBtn}
              onClick={() => editWeapon(r, (x) => ({ ...x, empty_magazine: category.emptyMagazine }))}>
              chargeur vide de la catégorie : {category.emptyMagazine}
            </button>
          ) : undefined,
        });
      }
      if (key === 'sound' || key === 'reload_sound') return textField(key, { placeholder: 'aucun son' });
      if (isParam(key) && table.params.find((p) => p.key === paramKey(key))?.flag) return flagField(key);
      return numberField(key);
    };

    const shown = new Set<string>();
    const groups = GROUPS.map((g) => {
      const fields = g.fields.filter((k) => (k === 'rate' ? true : applies(k)));
      fields.forEach((k) => shown.add(k));
      return { ...g, fields };
    }).filter((g) => g.fields.length > 0);

    // Tout ce que la fiche ne montre pas pour ce type d'arme reste réglable ici : rien n'est caché.
    const rest = [
      ...NUMBER_FIELDS.filter((k) => !shown.has(k)),
      ...(['ammo_item', 'empty_magazine', 'reload_sound'] as const).filter((k) => !shown.has(k)),
      ...table.params.map((p) => `params.${p.key}`).filter((k) => !shown.has(k)),
      ...(shown.has('extra_missiles') ? [] : ['extra_missiles']),
    ];
    const unknownParams = Object.entries(w.unknownParams);
    const edit = edits[r.key];

    return (
      <section className={base.detail} key={r.key}>
        <div className={styles.sheetHead}>
          <div className={styles.sheetTitle}>
            <h2>{w.name || r.id}</h2>
            <code>{r.id} · {w.item || 'sans objet'}</code>
            {rowBadges(r)}
          </div>
          <div className={styles.sheetActions}>
            {edit && (
              <button className={base.secondaryBtn} onClick={() => revertWeapon(r)}
                title="Oublie ce que la page a changé sur cette arme : retour à ce qui est dans le fichier">
                <Undo2 size={14} /> Annuler mes modifications
              </button>
            )}
            {r.def && (r.entry !== null || r.differsFromDefault) && (
              <button className={base.secondaryBtn} onClick={() => resetWeapon(r)}
                title="Reprend la définition par défaut du mod : l'entrée de l'arme quitte le fichier">
                <RotateCcw size={14} /> Retour aux valeurs par défaut
              </button>
            )}
            {r.origin === 'custom' && !r.removed && (
              <button className={base.secondaryBtn} onClick={() => removeWeapon(r)}>
                <Trash2 size={14} /> Retirer du fichier
              </button>
            )}
          </div>
        </div>

        {r.removed ? (
          <div className={base.warnBox}>
            <AlertTriangle size={14} /> Cette arme sur mesure sera retirée du fichier à l'enregistrement.
          </div>
        ) : (
          <>
            {r.origin === 'file' && !r.differsFromDefault && r.entry !== null && (
              <p className={base.muted}>
                Cette arme est écrite dans le fichier avec les valeurs par défaut du mod. « Retour aux valeurs par défaut »
                retire son entrée : elle suivra alors les valeurs par défaut de la version du mod de l'image.
              </p>
            )}
            {r.origin === 'custom' && (
              <p className={base.muted}>
                Arme sur mesure : le mod n'a pas de définition par défaut pour <code>{r.id}</code>, toute sa définition est dans le fichier.
              </p>
            )}
            {r.unknownKeys.length > 0 && (
              <p className={base.muted}>
                Clés du fichier que la page ne gère pas, gardées telles quelles : {r.unknownKeys.join(', ')}.
              </p>
            )}

            <div className={styles.group}>
              <h3 className={styles.groupTitle}>Identité</h3>
              <div className={styles.grid}>
                {textField('name', { placeholder: r.id })}
                {textField('item', { placeholder: `ng:${r.id}` })}
                <Field label="Type d'arme" title="kind" problem={problemOf('kind')}
                  help="Arme à feu : tir instantané. Lanceur de roquettes : roquette qui explose. Lance-missiles : tire un missile de l'inventaire."
                  onDefault={def && def.kind !== w.kind ? () => editWeapon(r, (x) => ({ ...x, kind: def.kind })) : undefined}
                  defaultText={def ? KIND_LABEL[def.kind] ?? def.kind : undefined}>
                  <div className={`${styles.inputUnit} ${problemOf('kind') ? styles.inputBad : ''}`}>
                    <select value={w.kind} onChange={(e) => editWeapon(r, (x) => ({ ...x, kind: e.target.value }))}>
                      {!table.kinds.includes(w.kind) && <option value={w.kind}>{w.kind || 'choisir'} (inconnu)</option>}
                      {table.kinds.map((k) => <option key={k} value={k}>{KIND_LABEL[k] ?? k}</option>)}
                    </select>
                  </div>
                </Field>
                <Field label="Catégorie" title="category" problem={problemOf('category')}
                  help="Catégorie de NationsGlory Java : elle donne le multiplicateur de tête, le chargeur vide et le son de rechargement habituels. En changer fait suivre ceux qui n'ont pas été réglés à la main."
                  onDefault={def && def.category !== w.category ? () => editWeapon(r, (x) => withCategory(x, def.category, table)) : undefined}
                  defaultText={def ? categoryLabel(def.category) : undefined}>
                  <div className={`${styles.inputUnit} ${problemOf('category') ? styles.inputBad : ''}`}>
                    <select value={w.category} onChange={(e) => editWeapon(r, (x) => withCategory(x, e.target.value, table))}>
                      {w.category && !category && <option value={w.category}>{w.category} (inconnue)</option>}
                      <option value="">Aucune</option>
                      {table.categories.map((c) => <option key={c.id} value={c.id}>{categoryLabel(c.id)}</option>)}
                    </select>
                  </div>
                </Field>
                <Field label="Arme active" title="enabled" help="Décochée, l'arme ne tire plus."
                  onDefault={def && def.enabled !== w.enabled ? () => editWeapon(r, (x) => ({ ...x, enabled: def.enabled })) : undefined}
                  defaultText={def?.enabled ? 'oui' : 'non'}>
                  <span className={styles.check}>
                    <input type="checkbox" checked={w.enabled} onChange={(e) => editWeapon(r, (x) => ({ ...x, enabled: e.target.checked }))} />
                    {w.enabled ? 'Oui' : 'Non'}
                  </span>
                </Field>
              </div>
            </div>

            {groups.map((g) => (
              <div key={g.title} className={styles.group}>
                <h3 className={styles.groupTitle}>{g.title}</h3>
                {g.note && <p className={styles.groupNote}>{g.note}</p>}
                <div className={styles.grid}>{g.fields.map(renderField)}</div>
              </div>
            ))}

            {unknownParams.length > 0 && (
              <div className={styles.group}>
                <h3 className={styles.groupTitle}>Clés de params inconnues du mod</h3>
                <p className={styles.groupNote}>
                  Sans effet : le mod les signale au chargement (souvent une faute de frappe). Gardées dans le fichier tant qu'elles ne sont pas retirées ici.
                </p>
                <div className={styles.chips}>
                  {unknownParams.map(([k, v]) => (
                    <span key={k} className={styles.check}>
                      <code>{k}</code> = {fmt(v)}
                      <button type="button" className={`${base.iconBtn} ${base.iconBtnDanger}`} title={`Retirer ${k} du fichier`}
                        onClick={() => editWeapon(r, (x) => ({
                          ...x,
                          unknownParams: Object.fromEntries(Object.entries(x.unknownParams).filter(([key]) => key !== k)),
                        }))}>
                        <Trash2 size={13} />
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            )}

            {rest.length > 0 && (
              <details className={`${styles.group} ${styles.others}`}>
                <summary>Autres réglages, sans effet pour {(KIND_LABEL[w.kind] ?? 'ce type d\'arme').toLowerCase()} ({rest.length})</summary>
                <p className={styles.groupNote}>
                  Le mod les lit et les borne quand même : ils restent réglables, et servent si le type de l'arme change.
                </p>
                <div className={styles.grid}>{rest.map(renderField)}</div>
              </details>
            )}
          </>
        )}
      </section>
    );
  };

  /** Pastilles d'état d'une arme : d'où viennent ses valeurs, et ce que l'enregistrement en fera. */
  const rowBadges = (r: WeaponRow) => {
    const problems = problemsByKey[r.key]?.length ?? 0;
    return (
      <span className={styles.badges}>
        {r.origin === 'custom' && <span className={styles.badge}>Sur mesure</span>}
        {r.origin !== 'custom' && !r.differsFromDefault && (
          <span className={styles.badge} title={r.entry ? 'Écrite dans le fichier avec les valeurs par défaut du mod' : 'Absente du fichier : valeurs par défaut du mod'}>
            Par défaut{r.entry ? ' (dans le fichier)' : ''}
          </span>
        )}
        {r.origin !== 'custom' && r.differsFromDefault && (
          <span className={`${styles.badge} ${styles.badgeAccent}`} title="Diffère de la définition par défaut du mod">Modifiée</span>
        )}
        {!r.value.enabled && !r.removed && <span className={styles.badge}>Désactivée</span>}
        {r.removed && <span className={`${styles.badge} ${styles.badgeWarn}`}>Retirée à l'enregistrement</span>}
        {r.edited && !r.removed && <span className={`${styles.badge} ${styles.badgeWarn}`}>À enregistrer</span>}
        {problems > 0 && <span className={`${styles.badge} ${styles.badgeBad}`}><AlertTriangle size={11} /> {problems} à corriger</span>}
      </span>
    );
  };

  // ==================== Règles communes ====================

  const renderRule = (key: string, table: WeaponsCatalog) => {
    const info = RULE_INFO[key] ?? { label: key, help: '' };
    const fallback = table.rules.defaults[key];
    const value = rules[key];
    const problem = rulesBad.find((p) => p.field === key)?.message;
    if (typeof fallback === 'boolean') {
      const on = value === true;
      return (
        <Field key={key} label={info.label} help={info.help} title={`weapon_rules.${key}`}
          onDefault={on !== fallback ? () => setRule(key, fallback) : undefined} defaultText={fallback ? 'oui' : 'non'}>
          <span className={styles.check}>
            <input type="checkbox" checked={on} onChange={(e) => setRule(key, e.target.checked)} />
            {on ? 'Oui' : 'Non'}
          </span>
        </Field>
      );
    }
    if (typeof fallback === 'number') {
      const n = typeof value === 'number' ? value : Number.NaN;
      const bound = table.rules.fields[key];
      let help = info.help;
      if (key === 'headshot_below_eye' && Number.isFinite(n)) {
        help = `${info.help} Ici : la tête commence à ${fmt(PLAYER_EYE_HEIGHT - n)} bloc au-dessus des pieds d'un joueur debout.`;
      }
      return (
        <Field key={key} label={info.label} bounds={boundText(bound)} help={help} title={`weapon_rules.${key}`} problem={problem}
          onDefault={!Object.is(n, fallback) ? () => setRule(key, fallback) : undefined} defaultText={fmt(fallback)}>
          <div className={`${styles.inputUnit} ${problem ? styles.inputBad : ''}`}>
            <NumInput value={n} step={bound?.integer ? 1 : 'any'} onChange={(v) => setRule(key, v)} />
            {info.unit && <span>{info.unit}</span>}
          </div>
        </Field>
      );
    }
    if (Array.isArray(fallback)) {
      const names = Array.isArray(value) ? value : [];
      const defNames = nameList(fallback);
      return (
        <Field key={key} label={info.label} help={info.help} title={`weapon_rules.${key}`} wide
          onDefault={!sameStrings(names, defNames) ? () => setRule(key, defNames) : undefined}
          defaultText={defNames.length > 0 ? defNames.join(', ') : 'aucun'}>
          <div className={styles.inputUnit}>
            <NamesInput value={names} placeholder="aucun" onChange={(v) => setRule(key, v)} />
          </div>
        </Field>
      );
    }
    const text = typeof value === 'string' ? value : '';
    const defText = typeof fallback === 'string' ? fallback : '';
    const choices = table.rules.choices[key];
    if (choices) {
      const labels = RULE_CHOICE_LABEL[key] ?? {};
      return (
        <Field key={key} label={info.label} help={info.help} title={`weapon_rules.${key}`} problem={problem}
          onDefault={text !== defText ? () => setRule(key, defText) : undefined} defaultText={labels[defText] ?? defText}>
          <div className={`${styles.inputUnit} ${problem ? styles.inputBad : ''}`}>
            <select value={text} onChange={(e) => setRule(key, e.target.value)}>
              {!choices.includes(text) && <option value={text}>{text || 'choisir'} (inconnue)</option>}
              {choices.map((c) => <option key={c} value={c}>{labels[c] ?? c}</option>)}
            </select>
          </div>
        </Field>
      );
    }
    return (
      <Field key={key} label={info.label} help={info.help} title={`weapon_rules.${key}`}
        onDefault={text !== defText ? () => setRule(key, defText) : undefined} defaultText={defText === '' ? 'vide' : defText}>
        <div className={styles.inputUnit}>
          <input value={text} placeholder="aucun" spellCheck={false} onChange={(e) => setRule(key, e.target.value)} />
        </div>
      </Field>
    );
  };

  const renderRules = (table: WeaponsCatalog) => {
    const known = RULE_GROUPS.flatMap((g) => g.fields);
    const others = Object.keys(table.rules.defaults).filter((k) => !known.includes(k));
    const rulesRaw = loaded?.weapon_rules;
    const unknownKeys = isObject(rulesRaw) ? Object.keys(rulesRaw).filter((k) => !(k in table.rules.defaults)) : [];
    const head = RULE_GROUPS[0];
    return (
      <section className={base.detail}>
        {loaded?.weapon_rules === undefined && (
          <p className={base.muted}>
            Le fichier n'a pas de section <code>weapon_rules</code> : le mod applique ses valeurs par défaut, affichées ici. Elle
            ne sera créée que si une règle change, avec cette seule règle.
          </p>
        )}
        {unknownKeys.length > 0 && (
          <p className={base.muted}>Clés du fichier que la page ne gère pas, gardées telles quelles : {unknownKeys.join(', ')}.</p>
        )}

        <div className={styles.group}>
          <h3 className={styles.groupTitle}>{head.title}</h3>
          <div className={styles.grid}>{head.fields.filter((k) => k in table.rules.defaults).map((k) => renderRule(k, table))}</div>
        </div>

        <div className={styles.group}>
          <h3 className={styles.groupTitle}>Multiplicateurs de tête par catégorie</h3>
          <p className={styles.groupNote}>
            Le multiplicateur d'une catégorie est fixé dans le mod, il n'a pas de clé dans le fichier : il vaut pour une arme
            dont l'entrée ne porte pas de multiplicateur. Régler ici une catégorie écrit ce multiplicateur dans chacune de ses
            armes. Il ne sert que pour une arme dont les dégâts à la tête valent 0 ; sinon ce sont ses dégâts à la tête qui comptent.
          </p>
          <div>
            <div className={`${styles.categoryRow} ${styles.categoryHead}`}>
              <span>Catégorie</span><span>Multiplicateur du mod</span><span>Multiplicateur de ses armes</span><span>Armes</span>
            </div>
            {table.categories.map((c) => {
              const members = rows.filter((r) => !r.removed && r.value.category === c.id);
              const values = [...new Set(members.map((r) => r.value.params.headshot_multiplier))];
              const common = values.length === 1 ? values[0] : Number.NaN;
              const bound = table.params.find((p) => p.key === 'headshot_multiplier');
              return (
                <div key={c.id} className={styles.categoryRow}>
                  <span><strong>{categoryLabel(c.id)}</strong></span>
                  <span className={styles.num}>× {fmt(c.headshotMultiplier)}</span>
                  <span>
                    <div className={styles.inputUnit}>
                      <NumInput value={common} disabled={members.length === 0} ariaLabel={`Multiplicateur de tête des armes de la catégorie ${categoryLabel(c.id)}`}
                        placeholder={members.length === 0 ? 'aucune arme' : 'valeurs différentes'}
                        onChange={(v) => setCategoryMultiplier(c.id, v)} />
                      <span>×</span>
                    </div>
                    {bound && <span className={styles.fieldHelp}>{boundText(bound)}</span>}
                    {members.length > 0 && !Object.is(common, c.headshotMultiplier) && (
                      <button type="button" className={styles.defaultBtn} onClick={() => setCategoryMultiplier(c.id, c.headshotMultiplier)}>
                        valeur du mod : {fmt(c.headshotMultiplier)}
                      </button>
                    )}
                  </span>
                  <span className={base.muted}>
                    {members.length === 0 ? 'Aucune arme dans cette catégorie.' : members.map((r) => {
                      const dedicated = r.value.params.headshot_damage > 0;
                      return `${r.value.name || r.id} : ${fmt(headshotDamage(r.value))} à la tête (${dedicated ? 'dégâts dédiés' : `× ${fmt(r.value.params.headshot_multiplier)}`})`;
                    }).join(' · ')}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        {RULE_GROUPS.slice(1).map((g) => (
          <div key={g.title} className={styles.group}>
            <h3 className={styles.groupTitle}>{g.title}</h3>
            <div className={styles.grid}>{g.fields.filter((k) => k in table.rules.defaults).map((k) => renderRule(k, table))}</div>
          </div>
        ))}
        {others.length > 0 && (
          <div className={styles.group}>
            <h3 className={styles.groupTitle}>Autres règles</h3>
            <div className={styles.grid}>{others.map((k) => renderRule(k, table))}</div>
          </div>
        )}
      </section>
    );
  };

  // ==================== Rendu ====================

  const editedCount = rows.filter((r) => r.edited).length;

  return (
    <div className={base.page}>
      <header className={base.header}>
        <div>
          <h1 className={base.title}>Armes</h1>
          <p className={base.subtitle}>
            Armes de RecubeICBM : dégâts, cadence, chargeurs, tirs à la tête, durabilité, mondes et règles communes. Commun à tous
            les containers du projet dont l'image porte RecubeICBM. Un changement arrive sur les serveurs à la publication de
            l'image (Envoyer), pas avant. Missiles, tourelle et le reste du fichier ne sont ni affichés ni modifiés ici.
          </p>
        </div>
        <div className={base.headerActions}>
          <button className={base.secondaryBtn} onClick={reload} disabled={loading}>
            {loading ? <Loader2 size={14} className={base.spin} /> : <RefreshCw size={14} />} Rafraîchir
          </button>
          <button className={base.primaryBtn} onClick={save} disabled={!dirty || saving || blocked}
            title={blocked
              ? "Corrige d'abord les valeurs signalées : le mod les refuserait"
              : 'Écrit les armes et les règles dans tous les containers du projet qui portent RecubeICBM'}>
            {saving ? <Loader2 size={14} className={base.spin} /> : <Save size={14} />} Enregistrer
          </button>
        </div>
      </header>

      <nav className={base.tabBar}>
        <button type="button" className={`${base.tabBtn} ${tab === 'weapons' ? base.tabBtnOn : ''}`}
          onClick={() => setTab('weapons')}>Armes{ready ? ` (${rows.length})` : ''}</button>
        <button type="button" className={`${base.tabBtn} ${tab === 'rules' ? base.tabBtnOn : ''}`}
          onClick={() => setTab('rules')}>Règles communes</button>
      </nav>

      {inspection && (
        <ContainerScope entries={inspection.entries} referencePath={inspection.referencePath}
          labelOf={(k) => SECTION_LABELS[k] ?? k} untouched={untouched} />
      )}

      {ready && gaps.map((g) => (
        <div key={g.image.containerId} className={g.kind === 'older' ? base.warnBox : styles.infoBox}>
          {g.kind === 'older' ? <AlertTriangle size={14} /> : <Info size={14} />}
          <span>
            {g.kind === 'older' && (
              <>
                L'image de <strong>{g.image.containerName}</strong> porte RecubeICBM {g.image.version} : cette page décrit
                la {catalog.tableVersion}. Tant que l'image n'est pas montée en {catalog.tableVersion}, le mod y ignore les armes
                et les réglages apparus depuis, et une arme absente du fichier y garde ses valeurs par défaut de
                la {g.image.version}, pas celles affichées ici.
              </>
            )}
            {g.kind === 'newer' && (
              <>
                L'image de <strong>{g.image.containerName}</strong> porte RecubeICBM {g.image.version}, plus récent que la table
                du panel ({catalog.tableVersion}) : les valeurs par défaut et les bornes affichées peuvent être en retard, la
                table du panel est à mettre à jour.
              </>
            )}
            {g.kind === 'unknown' && (
              <>
                Version de RecubeICBM illisible pour l'image de <strong>{g.image.containerName}</strong> (release-version.json) :
                les valeurs par défaut affichées sont celles de la {catalog.tableVersion}.
              </>
            )}
          </span>
        </div>
      ))}

      {status && (
        <div className={status.ok ? base.ok : base.error}>
          {status.ok ? <Check size={14} /> : <AlertTriangle size={14} />} {status.msg}
        </div>
      )}

      {report && <ApplyReport result={report} onClose={() => setReport(null)} />}

      {ready && list.invalid.length > 0 && (
        <div className={base.warnBox}>
          <AlertTriangle size={14} />
          <span>
            {list.invalid.length} entrée(s) de <code>weapons</code> que le mod ignore
            ({list.invalid.map((i) => `n° ${i.index + 1} ${i.reason}`).join(' ; ')}).{' '}
            {dropInvalid
              ? "Elles seront retirées à l'enregistrement. "
              : "Rien ne peut être enregistré tant qu'elles sont là. "}
            <button type="button" className={styles.defaultBtn} onClick={() => setDropInvalid(!dropInvalid)}>
              {dropInvalid ? 'Les garder' : 'Les retirer du fichier'}
            </button>
          </span>
        </div>
      )}

      {ready && unreadable.length > 0 && (
        <div className={base.warnBox}>
          <AlertTriangle size={14} />
          <span>
            Le fichier porte des valeurs que le mod ne sait pas lire (un texte pour un nombre, par exemple) : {unreadable.join(' ; ')}.
            Une seule suffit à lui faire rejeter tout le fichier. La page les remplace par les valeurs affichées : vérifie-les,
            puis enregistre.
          </span>
        </div>
      )}

      {ready && dirty && (blockingRows.length > 0 || rulesBad.length > 0) && (
        <div className={base.error}>
          <AlertTriangle size={14} />
          <span>
            À corriger avant d'enregistrer (le mod refuserait ces valeurs) :{' '}
            {[
              ...blockingRows.map((r) => `${r.value.name || r.id} (${problemsByKey[r.key].map((p) => FIELD_INFO[p.field]?.label ?? p.field).join(', ')})`),
              ...(rulesBad.length > 0 ? [`règles communes (${rulesBad.map((p) => RULE_INFO[p.field]?.label ?? p.field).join(', ')})`] : []),
            ].join(' ; ')}.
          </span>
        </div>
      )}

      {!ready ? (
        <section className={base.detail}>
          <div className={base.detailEmpty}>
            {loading ? (
              <><Loader2 size={22} className={base.spin} /><p>Ouverture de {WEAPONS_FILE}…</p></>
            ) : missing && missing.entries.length === 0 ? (
              <>
                <AlertTriangle size={22} />
                <p>{missing.message}</p>
                <p className={base.muted}>Déclare les containers du projet dans Paramètres généraux → Containers.</p>
              </>
            ) : missing ? (
              <NoReference file={WEAPONS_FILE} entries={missing.entries} mod={WEAPONS_MOD} />
            ) : (
              <>
                <Crosshair size={26} />
                {/* Une ouverture en échec a déjà posé son message au-dessus : ne pas le contredire. */}
                <p>{WEAPONS_FILE} n'a pas pu être ouvert.</p>
              </>
            )}
          </div>
        </section>
      ) : tab === 'weapons' ? (
        <>
          {loaded.weapons === undefined && (
            <p className={base.muted}>
              Le fichier n'a pas de section <code>weapons</code> : toutes les armes sont à leurs valeurs par défaut. Elle ne sera
              créée que si une arme est modifiée, avec cette seule arme.
            </p>
          )}
          <div className={styles.table}>
            <div className={styles.tableHead}>
              <span>Arme</span>
              <span>Catégorie</span>
              <span>Dégâts <em>(points de vie)</em></span>
              <span>Tête <em>(points de vie)</em></span>
              <span>Cadence <em>(tirs par seconde)</em></span>
              <span>Chargeur <em>(balles)</em></span>
              <span>Portée <em>(blocs)</em></span>
              <span>Recharge <em>(secondes)</em></span>
              <span>État</span>
            </div>
            {rows.map((r) => {
              const w = r.value;
              const gun = w.kind === GUN;
              const rate = fireRate(w.cooldown_seconds);
              const pellets = w.params.pellets ?? 1;
              return (
                <button key={r.key} type="button" aria-pressed={r.key === selected}
                  className={`${styles.tableRow} ${r.key === selected ? styles.tableRowOn : ''} ${!w.enabled || r.removed ? styles.tableRowOff : ''}`}
                  onClick={() => setSelected(r.key === selected ? null : r.key)}>
                  <span className={styles.weaponName}>
                    <strong>{w.name || r.id}</strong>
                    <code>{r.id}</code>
                  </span>
                  <span>{gun ? categoryLabel(w.category) : KIND_LABEL[w.kind] ?? w.kind}</span>
                  <span className={styles.num}>{fmt(w.damage)}{gun && pellets > 1 ? ` × ${fmt(pellets)} plombs` : ''}</span>
                  <span className={styles.num}>{gun ? fmt(headshotDamage(w)) : '–'}</span>
                  <span className={styles.num}>{rate === null ? '–' : fmt(rate)}{isAutomatic(w) ? ' auto' : ''}</span>
                  <span className={styles.num}>{gun ? fmt(w.magazine) : '–'}</span>
                  <span className={styles.num}>{fmt(w.range)}</span>
                  <span className={styles.num}>{gun ? fmt(w.reload_seconds) : '–'}</span>
                  {rowBadges(r)}
                </button>
              );
            })}
          </div>
          <div className={base.footerActions}>
            <span className={base.muted}>
              {row ? 'Clique de nouveau sur la ligne pour fermer la fiche.' : "Clique sur une arme pour ouvrir sa fiche."}
            </span>
            <span className={base.muted}>
              {dirty ? `Modifications non enregistrées${editedCount > 0 ? ` (${editedCount} arme(s))` : ''}.` : 'Enregistré.'}
            </span>
          </div>
          {row && renderSheet(row, catalog)}
        </>
      ) : (
        <>
          {renderRules(catalog)}
          <div className={base.footerActions}>
            <span />
            <span className={base.muted}>{dirty ? 'Modifications non enregistrées.' : 'Enregistré.'}</span>
          </div>
        </>
      )}
    </div>
  );
}
